import React, { useContext } from 'react';
import { ModuleSectionHeader, ModuleSectionItems, ModuleSectionRoot } from './ModuleSectionComponents';
import PrioritizationContext from 'pages/Zones/PrioritizationContext/PrioritizationContext';

import { FlightRadioButton, FlightSelect, FlightToggleSwitch } from '@flybits/design-system';

import { ReactComponent as Deprioritized } from 'assets/icons/deprioritize-content.svg';

import './HideContentSection.scss';
import { Module } from 'pages/Zones/types';
import StatusBadge, { STATUS_TYPES } from 'components/Shared/StatusBadge/StatusBadge';

const MAIN_CLASS = 'hide-content-section';
const ROW_CLASS = `${MAIN_CLASS}--row`;
const ROW_INPUTS_CLASS = `${ROW_CLASS}--inputs`;
const ROW_RADIO_GROUP_CLASS = `${ROW_CLASS}--radio-group`;

const DISMISS_OPTIONS = [
  { key: 'dismiss', name: 'Permanently hide' },
  { key: 'deprioritize', name: 'Move to end of queue' },
];

type HideContentHeader = {
  allowHide: boolean;
  handleOnChangeToggle: (newValue: boolean) => void;
};
function HideContentHeader({ allowHide, handleOnChangeToggle }: HideContentHeader) {
  return (
    <div className="labeled-section">
      <ModuleSectionHeader
        icon={<Deprioritized />}
        title={'Hide Content'}
        subtext={<>Enable this feature to allow end-user to decide if they want hide content</>}
      />
      <FlightToggleSwitch className="deprioritized-section" checked={!!allowHide} onChange={handleOnChangeToggle} />
    </div>
  );
}

type HideContentSection = {
  sectionNumber: number;
  module: Module;
};
function HideContentSection({ sectionNumber, module }: HideContentSection) {
  const { updateModule, selectedZone } = useContext(PrioritizationContext);
  const { contentSuppressionConfig } = module;
  const { dismiss, deprioritize } = contentSuppressionConfig || {};
  const dismissEnabled = !!dismiss?.enabled;
  const deprioritizeEnabled = !!deprioritize?.enabled;
  const allowHide = dismissEnabled || deprioritizeEnabled;

  const handleOnChangeToggle = (newValue: boolean) =>
    selectedZone.id &&
    updateModule(selectedZone.id, {
      ...module,
      contentSuppressionConfig: {
        dismiss: { enabled: newValue, scope: dismiss ? dismiss.scope : 'all' },
        deprioritize: { enabled: false, scope: 'all' },
      },
    });

  const handleOnChangeDismissOption = (option: (typeof DISMISS_OPTIONS)[number]) => {
    selectedZone.id &&
      updateModule(selectedZone.id, {
        ...module,
        contentSuppressionConfig: {
          dismiss: {
            enabled: option.key === 'dismiss',
            scope: dismiss ? dismiss.scope : 'all',
          },
          deprioritize: {
            enabled: option.key === 'deprioritize',
            scope: deprioritize ? deprioritize.scope : 'all',
          },
        },
      });
  };

  const handleOnChangeDismissFromAll = (valueKey: 'all' | 'current') => {
    selectedZone.id &&
      updateModule(selectedZone.id, {
        ...module,
        contentSuppressionConfig: {
          dismiss: {
            enabled: dismissEnabled,
            scope: valueKey === 'current' ? 'current' : 'all',
          },
          deprioritize: {
            enabled: deprioritizeEnabled,
            scope: valueKey === 'current' ? 'current' : 'all',
          },
        },
      });
  };

  return (
    <ModuleSectionRoot sectionNumber={sectionNumber}>
      <HideContentHeader allowHide={allowHide} handleOnChangeToggle={handleOnChangeToggle} />
      {allowHide && (
        <ModuleSectionItems>
          <div className={`${MAIN_CLASS}`}>
            <div className={`${ROW_CLASS}`}>
              <span>Choose what happens when a user dismisses a card</span>
              <div className={`${ROW_INPUTS_CLASS}`}>
                <FlightSelect
                  options={DISMISS_OPTIONS}
                  selected={dismissEnabled ? DISMISS_OPTIONS[0] : DISMISS_OPTIONS[1]}
                  handleOptionClick={handleOnChangeDismissOption}
                />
              </div>
            </div>
            <div className={`${ROW_CLASS}`}>
              <span>If hidden, hide this content from</span>
              <div className={`${ROW_INPUTS_CLASS}`}>
                <div className={`${ROW_RADIO_GROUP_CLASS}`}>
                  <FlightRadioButton
                    value="all"
                    label="All modules"
                    checked={
                      dismissEnabled
                        ? dismiss.scope === 'all' || !dismiss.scope
                        : deprioritize
                        ? deprioritize.scope === 'all' || !deprioritize.scope
                        : true
                    }
                    onSelect={handleOnChangeDismissFromAll}
                  />
                  <StatusBadge status={STATUS_TYPES.STATUS_AUTO_MAPPED} title="Recommended" />
                </div>
                <div className={`${ROW_RADIO_GROUP_CLASS}`}>
                  <FlightRadioButton
                    value="current"
                    label="Current module only (Still appear in other modules)"
                    checked={
                      dismissEnabled
                        ? dismiss.scope === 'current'
                        : deprioritize
                        ? deprioritize.scope === 'current'
                        : false
                    }
                    onSelect={handleOnChangeDismissFromAll}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModuleSectionItems>
      )}
    </ModuleSectionRoot>
  );
}

export default HideContentSection;
